import { createContext, useCallback, useEffect, useMemo } from 'react';

import useAppSelector from '@webclient/hooks/useAppSelector';

import _ from 'lodash';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';

const LATEST_MESSAGES_LIMIT = 100;

export const StateUpdateContext = createContext<{
  handleSubscribe: (subscription: StateUpdateSubscriptionType) => () => void;
} | null>(null);

type MessageType = {
  type: string;
  error?: string;
  data?: unknown;
};

type StateUpdateSubscriptionType = {
  type: MessageType['type'] | RegExp;
  keepCase?: boolean;
  processLatestMessages?: boolean;
  callback: (message: MessageType) => void;
  onError?: (message: MessageType) => void;
};

export default function StateUpdateProvider({
  children,
}: React.PropsWithChildren) {
  const latestMessages: MessageType[] = useMemo(() => [], []);
  const subscriptions: StateUpdateSubscriptionType[] = useMemo(() => [], []);
  const me = useAppSelector((state) => state.auth.me);
  const { socket } = useSocket(import.meta.env.VITE_KNOWZ_WEB_SOCKET_URL, {
    enabled: true,
    transports: ['websocket', 'polling'],
  });

  function handleMessage(
    subscription: StateUpdateSubscriptionType,
    message: MessageType,
    camelizedMessage: MessageType,
  ) {
    const m = subscription.keepCase ? message : camelizedMessage;

    const shouldHandle =
      subscription.type instanceof RegExp
        ? subscription.type.test(m.type)
        : subscription.type === m.type;

    if (shouldHandle) {
      if ('error' in m) {
        subscription.onError?.(m);
      } else {
        subscription.callback(m);
      }
    }
  }

  useEffect(() => {
    if (me?.uuid) {
      socket.emit('subscribe', `${me?.uuid}_state_update`);
    }
  }, [me, socket]);

  useSocketEvent(socket, 'message', {
    onMessage: async (message: MessageType) => {
      const camelizedMessage = camelizeKeys(message) as MessageType;
      latestMessages.push(message);
      if (latestMessages.length > LATEST_MESSAGES_LIMIT) {
        latestMessages.shift();
      }

      for (const subscription of subscriptions) {
        try {
          handleMessage(subscription, message, camelizedMessage);
        } catch (error) {
          console.error('Error in subscription callback:', error);
        }
      }
    },
  });

  const handleSubscribe = useCallback(
    (subscription: StateUpdateSubscriptionType) => {
      subscriptions.push(subscription);

      if (subscription.processLatestMessages) {
        for (const message of latestMessages) {
          const camelizedMessage = camelizeKeys(message) as MessageType;
          handleMessage(subscription, message, camelizedMessage);
        }
      }

      return () => {
        const index = subscriptions.indexOf(subscription);

        if (index > -1) {
          subscriptions.splice(index, 1);
        }
      };
    },
    [latestMessages, subscriptions],
  );

  const value = useMemo(
    () => ({
      handleSubscribe,
    }),
    [handleSubscribe],
  );

  return (
    <StateUpdateContext.Provider value={value}>
      {children}
    </StateUpdateContext.Provider>
  );
}

function camelizeKeys(obj: unknown): unknown {
  if (_.isArray(obj)) {
    return obj.map(camelizeKeys);
  }

  if (_.isPlainObject(obj)) {
    return _.transform(
      obj as object,
      (result, value, key) => {
        result[_.camelCase(key)] = camelizeKeys(value);
      },
      {},
    );
  }

  return obj;
}
